import _ from 'underscore';

import {assert} from 'Asserts';
import {
  ColumnDefinition,
  ContainersColumnDefinition,
  getColumnDefinitionIndex,
  ParentStorableColumnDefinition,
} from 'notebook/structured-tables/columns/ColumnDefinitions';
import {CellError, toSpreadsheetIndex, ValueError} from 'notebook/tables/excess/Util';
import {CustomResolutionQueryType} from 'notebook/tables/string-resolution/StringResolutionConstants';
import {Cell, TableColumnType} from 'notebook/tables/TableConstants';
import {ResolutionQueryType} from 'notebook/tables/TableStoreTransforms';
import {formatTypedValue} from 'notebook/tables/typed-values/DataTypes';
import {BaseTypedValueType, TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';
import {
  makeTypedValueFromCustomResolvedValue,
  resolveNumberOfContainersTypedValue,
  resolveParentStorableTypedValue,
  ResolveQueryFn,
} from 'notebook/tables/typed-values/TypedValueResolution';
import {
  FinalArrayTypedValue,
  GridCoordinatesTypedValue,
  NumberTypedValue,
  StorableInfo,
} from 'notebook/tables/typed-values/TypedValueTypes';
import {TransferTableColumnGroup} from 'notebook/transfer-tables/TransferTableConstants';
import {splitCommaSeparatedString} from 'notebook/transfer-tables/TransferTableEditableRowsUtil';
import {ParentStorableType} from 'samples/storables/StorableSchemaConstants';

/**
 * Calculate the containers to show in the bottom half of a non-grid table, which are always new containers.
 */
export function setCustomEvalForNonGridContainers(
  columnDefinitions: Array<ColumnDefinition>,
  cellValue: Cell,
  rowIndex: number
): Cell {
  const numContainersColumnIndex = getColumnDefinitionIndex({
    columnDefinitionType: TableColumnType.NUMBER_OF_CONTAINERS,
    tableColumnDefinitions: columnDefinitions,
  });
  return {
    ...cellValue,
    customEvalArgs: [
      `=${toSpreadsheetIndex({
        row: rowIndex,
        column: numContainersColumnIndex,
      })}`,
    ],
    customEval: nonGridContainersCustomEval,
  };
}

export function nonGridContainersCustomEval(
  containersTypedValue: TypedValue,
  containersColumnDefinition: ContainersColumnDefinition,
  [numContainersTypedValue]: [TypedValue]
): {val: TypedValue; error: ValueError | null; locked: boolean} {
  // The evaluator currently does not return the error, so we need to resolve the cell ourselves.
  // In the future we can consider making the evaluator return the error, but for now this is a rare
  // enough case that it makes sense to handle individually.
  // The evaluator does return the resolved value.
  const {error} = resolveNumberOfContainersTypedValue(numContainersTypedValue);
  if (error) {
    return {
      val: containersTypedValue,
      error: null,
      locked: false,
    };
  }

  assert(
    numContainersTypedValue.type === BaseTypedValueType.NUMBER,
    'Expected resolution to throw an error if number of containers cannot be converted to type NUMBER'
  );

  // eslint-disable-next-line benchlint/no-in-operator
  if ('rawValue' in containersTypedValue && containersTypedValue.rawValue) {
    // User has entered custom barcodes
    return customBarcodeContainersCustomEval(containersTypedValue, numContainersTypedValue);
  }

  const numContainers = numContainersTypedValue.rawValue;
  assert(
    Number.isInteger(numContainers) && numContainers >= 1,
    `Unexpected number of containers: ${numContainers}. It should have failed prior validation.`
  );
  return {
    val: {
      type: BaseTypedValueType.STORABLE_PLACEHOLDERS,
      storablePlaceholders: _.range(numContainers).map(() => null),
    },
    error: null,
    locked: false,
  };
}

function toMaybeColumnFormula(columnIndex: number, rowIndex: number): string | null {
  if (columnIndex === -1) {
    return null;
  }
  return `=${toSpreadsheetIndex({
    row: rowIndex,
    column: columnIndex,
  })}`;
}

/**
 * Calculate the containers to show in the bottom half of a grid table, which may be a mix of new and
 * existing containers
 */
export function setCustomEvalForGridContainers(
  columnDefinitions: Array<ColumnDefinition>,
  cellValue: Cell,
  rowIndex: number
): Cell {
  const parentStorableColumnIndex = getColumnDefinitionIndex({
    columnDefinitionType: TableColumnType.PARENT_STORABLE,
    tableColumnDefinitions: columnDefinitions,
  });
  const positionColumnIndex = getColumnDefinitionIndex({
    columnGroup: TransferTableColumnGroup.TARGET,
    columnDefinitionType: TableColumnType.POSITION,
    tableColumnDefinitions: columnDefinitions,
  });
  const numContainersColumnIndex = getColumnDefinitionIndex({
    columnDefinitionType: TableColumnType.NUMBER_OF_CONTAINERS,
    tableColumnDefinitions: columnDefinitions,
  });
  const sourceContainerColumnIndex = getColumnDefinitionIndex({
    columnGroup: TransferTableColumnGroup.SOURCE,
    columnDefinitionType: TableColumnType.CONTAINERS,
    tableColumnDefinitions: columnDefinitions,
    isOptional: true,
  });
  const sourcePlateColumnIndex = getColumnDefinitionIndex({
    columnGroup: TransferTableColumnGroup.SOURCE,
    columnDefinitionType: TableColumnType.PLATE,
    tableColumnDefinitions: columnDefinitions,
    isOptional: true,
  });
  const sourcePositionColumnIndex = getColumnDefinitionIndex({
    columnGroup: TransferTableColumnGroup.SOURCE,
    columnDefinitionType: TableColumnType.POSITION,
    tableColumnDefinitions: columnDefinitions,
    isOptional: true,
  });
  const customEvalArgs = [
    `=${toSpreadsheetIndex({
      row: rowIndex,
      column: parentStorableColumnIndex,
    })}`,
    `=${toSpreadsheetIndex({
      row: rowIndex,
      column: positionColumnIndex,
    })}`,
    `=${toSpreadsheetIndex({
      row: rowIndex,
      column: numContainersColumnIndex,
    })}`,
    toMaybeColumnFormula(sourceContainerColumnIndex, rowIndex),
    toMaybeColumnFormula(sourcePlateColumnIndex, rowIndex),
    toMaybeColumnFormula(sourcePositionColumnIndex, rowIndex),
  ];
  return {
    ...cellValue,
    customEvalArgs,
    customEval: gridContainersCustomEval,
    customEvalOptions: {
      parentStorableColumnDefinition: columnDefinitions[parentStorableColumnIndex],
    },
  };
}

/**
 * Return the containers at the given positions of the given grid.
 *
 * This function performs preliminary validation of the dependency cells (position and grid), then delegates
 * to postValidationGridContainersCustomEval for the core logic.
 */
export function gridContainersCustomEval(
  containersTypedValue: TypedValue,
  containersColumnDefinition: ContainersColumnDefinition,
  [
    parentStorableTypedValue,
    positionTypedValue,
    numContainersTypedValue,
    sourceContainerTypedValue,
    sourcePlateTypedValue,
    sourcePositionTypedValue,
  ]: [TypedValue, TypedValue, TypedValue, TypedValue | null, TypedValue | null, TypedValue | null],
  {
    parentStorableColumnDefinition,
  }: {
    parentStorableColumnDefinition: ParentStorableColumnDefinition;
  },
  resolveFromResolutionQuery: ResolveQueryFn
): {val: TypedValue; error: CellError | null; locked: boolean} {
  // The evaluator currently does not return the error, so we need to resolve the cell ourselves.
  // In the future we can consider making the evaluator return the error, but for now this is a rare
  // enough case that it makes sense to handle individually.
  // The evaluator does return the resolved value.
  const {error: numContainersError} = resolveNumberOfContainersTypedValue(numContainersTypedValue);
  const {error: parentStorableError} = resolveParentStorableTypedValue(
    resolveFromResolutionQuery,
    parentStorableTypedValue,
    {
      isForTransfer: true,
      parentStorableType: ParentStorableType.GRID,
      containerSchemaId: parentStorableColumnDefinition.storableSchema
        ? parentStorableColumnDefinition.storableSchema.id
        : null,
    }
  );
  // Stop evaluation if any of the dependencies have errors.
  if (numContainersError != null || parentStorableError != null) {
    return {
      val: containersTypedValue,
      error: null,
      locked: false,
    };
  }

  assert(
    parentStorableTypedValue.type === BaseTypedValueType.STORABLES,
    'Expected resolution to throw an error if parent storable cannot be converted to type STORABLES'
  );
  assert(
    numContainersTypedValue.type === BaseTypedValueType.NUMBER,
    'Expected resolution to throw an error if number of containers cannot be converted to type NUMBER'
  );

  const positionResolvedValue = resolveFromResolutionQuery(
    positionTypedValue,
    {
      type: ResolutionQueryType.CUSTOM,
      customResolutionQueryType: CustomResolutionQueryType.LEGACY_TRANSFER_TABLE_VALIDATE_GRID_COORDINATES,
      positionTypedValue,
      parentStorableTypedValue,
    },
    makeTypedValueFromCustomResolvedValue
  );
  if (positionResolvedValue.error) {
    return {
      val: containersTypedValue,
      error: null,
      locked: false,
    };
  }

  const parsedPositions = {
    positions: (positionResolvedValue.resolvedValue as FinalArrayTypedValue).rawValue.map((val) => {
      const gridCoordinate = val as GridCoordinatesTypedValue;
      return {
        rowIndex: gridCoordinate.gridCoordinates.rowIndex,
        columnIndex: gridCoordinate.gridCoordinates.columnIndex,
      };
    }),
  };

  if (
    sourcePlateTypedValue &&
    sourcePositionTypedValue &&
    sourcePlateTypedValue.type === BaseTypedValueType.STORABLES
  ) {
    const sourcePositionResolvedValue = resolveFromResolutionQuery(
      sourcePositionTypedValue,
      {
        type: ResolutionQueryType.CUSTOM,
        customResolutionQueryType: CustomResolutionQueryType.LEGACY_TRANSFER_TABLE_VALIDATE_GRID_COORDINATES,
        positionTypedValue: sourcePositionTypedValue,
        parentStorableTypedValue: sourcePlateTypedValue,
      },
      makeTypedValueFromCustomResolvedValue
    );
    if (sourcePositionResolvedValue.error == null) {
      const parsedSourcePositions = {
        positions: (positionResolvedValue.resolvedValue as FinalArrayTypedValue).rawValue.map((val) => {
          const gridCoordinate = val as GridCoordinatesTypedValue;
          return {
            rowIndex: gridCoordinate.gridCoordinates.rowIndex,
            columnIndex: gridCoordinate.gridCoordinates.columnIndex,
          };
        }),
      };

      for (const sourcePosition of parsedSourcePositions.positions) {
        if (parsedPositions.positions.includes(sourcePosition)) {
          return {
            val: containersTypedValue,
            error: new ValueError('Target container must be different from source container'),
            locked: false,
          };
        }
      }
    }
  }

  const validatedContainersResolvedValue = resolveFromResolutionQuery(
    containersTypedValue,
    {
      type: ResolutionQueryType.CUSTOM,
      customResolutionQueryType:
        CustomResolutionQueryType.LEGACY_TRANSFER_TABLE_VALIDATED_DESTINATION_CONTAINERS,
      containersTypedValue,
      parentStorableTypedValue,
      numContainersTypedValue,
      sourceContainerTypedValue,
      positions: parsedPositions.positions,
    },
    makeTypedValueFromCustomResolvedValue
  );
  if (validatedContainersResolvedValue.error) {
    return {
      val: containersTypedValue,
      error: validatedContainersResolvedValue.error,
      locked: false,
    };
  }

  return {
    val: validatedContainersResolvedValue.resolvedValue,
    error: null,
    locked:
      validatedContainersResolvedValue.resolvedValue.type === BaseTypedValueType.STORABLE_PLACEHOLDERS &&
      // eslint-disable-next-line no-restricted-syntax
      _.some(validatedContainersResolvedValue.resolvedValue.storablePlaceholders),
  };
}

/**
 * Given the existing containers or placeholders in the parent storable, validate the transfer and return
 * the final TypedValue for the Containers cell. This method is called by the
 * fetchGridAndValidateContainers custom resolution query.
 */
export function postValidationGridContainersCustomEval(
  containersTypedValue: TypedValue,
  numContainersTypedValue: NumberTypedValue,
  sourceContainerTypedValue: TypedValue | null,
  storablePlaceholders: Array<StorableInfo | null>
): {val: TypedValue; error: ValueError | null; locked: boolean} {
  if (sourceContainerTypedValue && sourceContainerTypedValue.type === BaseTypedValueType.STORABLES) {
    const sourceContainer = sourceContainerTypedValue.storableSummaries[0];
    if (
      storablePlaceholders.some(
        (placeholderContainer) =>
          placeholderContainer != null && placeholderContainer.id === sourceContainer.id
      )
    ) {
      return {
        val: containersTypedValue,
        error: new ValueError('Target container must be different from source container'),
        locked: false,
      };
    }
  }

  // eslint-disable-next-line benchlint/no-in-operator
  if ('rawValue' in containersTypedValue && containersTypedValue.rawValue) {
    // User has entered custom barcodes
    // eslint-disable-next-line no-restricted-syntax
    if (_.some(storablePlaceholders)) {
      return {
        val: containersTypedValue,
        error: new ValueError('Cannot set barcodes for positions with existing containers.'),
        locked: false,
      };
    }
    return customBarcodeContainersCustomEval(containersTypedValue, numContainersTypedValue);
  } else if (formatTypedValue(containersTypedValue)) {
    return {
      val: containersTypedValue,
      error: new ValueError(`"${formatTypedValue(containersTypedValue)}" is not a valid barcode.`),
      locked: false,
    };
  }
  return {
    val: {
      type: BaseTypedValueType.STORABLE_PLACEHOLDERS,
      storablePlaceholders,
    },
    error: null,
    // eslint-disable-next-line no-restricted-syntax
    locked: _.some(storablePlaceholders),
  };
}

function customBarcodeContainersCustomEval(
  containersTypedValue: TypedValue,
  numContainersTypedValue: NumberTypedValue
): {val: TypedValue; error: ValueError | null; locked: false} {
  const formattedValue = formatTypedValue(containersTypedValue);
  const barcodeStrings = splitCommaSeparatedString(formattedValue);
  // eslint-disable-next-line no-restricted-syntax
  if (!_.every(barcodeStrings)) {
    return {
      val: containersTypedValue,
      error: new ValueError('"" is not a valid barcode.'),
      locked: false,
    };
  }
  if (barcodeStrings.length !== numContainersTypedValue.rawValue) {
    return {
      val: containersTypedValue,
      error: new ValueError('Number of barcodes must match number of containers.'),
      locked: false,
    };
  }
  return {
    val: {
      type: BaseTypedValueType.STRING,
      rawValue: formattedValue,
    },
    error: null,
    locked: false,
  };
}
